import { LitElement } from '@lit'
import {
  viewportObserver,
  debounce,
} from '@nova/utils'

class NovaProductGallery extends LitElement {
  static properties = {
    viewport: { type: String, state: true },
    galleryCarousel: { type: Object, state: true },
    thumbnailCarousel: { type: Object, state: true },
    thumbnailPrevious: { type: Object, state: true },
    thumbnailNext: { type: Object, state: true },
    zoomInOverlay: { type: Object, state: true },
  }

  constructor() {
    super()
    this.viewport = viewportObserver.currentViewport
    this.galleryCarousel = null
    this.thumbnailCarousel = null
    this.thumbnailPrevious = null
    this.thumbnailNext = null
    this.zoomInOverlay = null
    this.observer = null
  }

  connectedCallback() {
    super.connectedCallback()

    document.addEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })

    document.addEventListener('nova:product:render-gallery-arrows', (event) => {
      this.hideArrows()
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    document.removeEventListener('nova:product:render-gallery', (event) => {
      this.updateGalleryContent(event.detail.html)
    })

    document.removeEventListener('nova:product:render-gallery-arrows', (event) => {
      this.hideArrows()
    })

    if (this.observer) {
      this.observer.disconnect()
    }
  }

  updated(params) {
    const changedProperties = Array.from(params, ([key, value]) => ({ key, value }))
    if (changedProperties.length === 1 && changedProperties[0].key === 'zoomInOverlay') return
    
    this.galleryCarousel = this.querySelector('.js-gallery-carousel')
    this.thumbnailCarousel = this.querySelector('thumbnails-carousel')

    this.handleGalleryCarouselMove()
    this.handleZoomOnImage(3)
    this.galleryCarousel.goToSlide(0)

    document.dispatchEvent(
      new CustomEvent('nova:product:render-gallery-arrows')
    )
  }

  hideArrows() {
    setTimeout(() => {
      const allArrows = [...this.querySelector('sl-carousel').shadowRoot.querySelectorAll('.carousel__navigation-button:not(.carousel__navigation-button--disabled)')]
      allArrows.forEach(item => item.style.opacity = '1')
  
      const disabledArrows = [...this.querySelector('sl-carousel').shadowRoot.querySelectorAll('.carousel__navigation-button--disabled')]
      disabledArrows.forEach(item => item.style.opacity = '0')
    }, 150)
  }

  updateGalleryContent(content) {
    if (!content) return
    // Clear existing children
    while (this.firstChild) {
      this.removeChild(this.firstChild)
    }
    // Append new children
    while (content.firstChild) {
      this.appendChild(content.firstChild)
    }
    this.requestUpdate()
  }

  handleGalleryCarouselMove() {
    if (!this.galleryCarousel) return

    this.galleryCarousel.addEventListener('sl-slide-change', (event) => {
      if (!this.thumbnailCarousel) return

      const activeGalleryIndex = event.detail.index
      this.handleVideoAutoPlay(event.detail.slide)
      this.thumbnailCarousel.changeActiveSlide(Number(activeGalleryIndex))

      document.dispatchEvent(
        new CustomEvent('nova:product:render-gallery-arrows')
      )
    })
  }

  handleVideoAutoPlay(slide) {
    const media = slide.querySelector('nova-adaptive-video')
    if (!media) return
    media.play()
  }

  zoomMoveWithHover(image, event, zoomRatio) {
    const ratio = image.height / image.width
    const container = event.target.getBoundingClientRect()
    const xPosition = event.clientX - container.left
    const yPosition = event.clientY - container.top
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`

    this.zoomInOverlay.style.backgroundPosition = `${xPercent} ${yPercent}`
    this.zoomInOverlay.style.backgroundSize = `${image.width * zoomRatio}px`
  }

  handleZoomEvents(image, zoomRatio) {
    this.zoomInOverlay = image.closest('.img').querySelector('.zoom-overlay')
    if (!this.zoomInOverlay) return
    this.zoomInOverlay.classList.remove('hidden')

    const carouselItem = image.closest('.gallery-item')
    if (carouselItem && !carouselItem.classList.contains('zoomed'))
      carouselItem.classList.add('zoomed')

    this.zoomInOverlay.addEventListener(
      'click',
      this.handleOverlayRemove.bind(this, carouselItem),
    )
    this.zoomInOverlay.addEventListener('mousemove', (event) =>
      this.zoomMoveWithHover(image, event, zoomRatio),
    )
    this.zoomInOverlay.addEventListener(
      'mouseleave',
      this.handleOverlayRemove.bind(this, carouselItem),
    )
  }

  handleOverlayRemove(carouselItem) {
    if (
      this.zoomInOverlay &&
      !this.zoomInOverlay.classList.contains('hidden')
    ) {
      this.zoomInOverlay.classList.add('hidden')

      this.zoomInOverlay.removeEventListener(
        'click',
        this.handleOverlayRemove.bind(this, carouselItem),
      )
      this.zoomInOverlay.removeEventListener(
        'mousemove',
        this.zoomMoveWithHover,
      )
      this.zoomInOverlay.removeEventListener(
        'mouseleave',
        this.handleOverlayRemove.bind(this, carouselItem),
      )

      this.zoomInOverlay = null
    }
    if (carouselItem) carouselItem.classList.remove('zoomed')
  }

  handleZoomOnImage(zoomRatio) {
    const images = this.querySelectorAll('.zoom-image')
    for (const image of images) {
      image.addEventListener('click', () => {
        this.handleZoomEvents(image, zoomRatio)
      })
    }
  }

  createRenderRoot() {
    return this
  }
}

customElements.define('nova-product-gallery', NovaProductGallery)
